import { create } from 'zustand';
import { validateSecurityCusip, fetchAccounts, fetchPMUsers as fetchPMUsersApi, fetchPMAccounts as fetchPMAccountsApi } from '../http/api';

// Helper: returns current UTC date-time string in "YYYY-MM-DDTHH:MM:SS" (derived from toISOString -> UTC).
// This format omits the trailing 'Z' and is safe to use in datetime-local inputs while ensuring the value is the current UTC time.
const getCurrentUTCDateTimeLocalString = () => {
  return new Date().toISOString().slice(0, 19);
};

export const useTradeBlotterOrderStore = create((set, get) => ({
  // Trade list and UI state
  trades: [],
  loading: false, // list loading
  error: null,

  // Trade entry form state (mirrors actualOrderStore)
  tradeEntryData: {
    account: '',
    cusip: '',
    cusipRef: '', // track canonical cusip returned by API
    side: '',
    tradeQuantity: '',
    tradePrice: '',
    executionDateTime: getCurrentUTCDateTimeLocalString(), // <-- use UTC-only helper
    counterPartyId: '', // previously counterparty
    settlementDate: '',
    comments: '',
    accruedInterest: '',
    principalAmount: '',
    estimatedTotal: '',
    // Keep existing fields for compatibility
    // pmId: null,
    userId: null,
    // pm: '',
    bondInventoryId: null,
    secMasterId: null,
    tradeTypeCode: 1, // Default to 1 as per example
    // action and tradeId are handled during submission, not stored in form data
  },
  accountIdentifiers: [],
  bondDetails: null,
  isEntryLoading: false, // entry-specific loading
  cusipError: '',
  fieldErrors: {},
  pmUsers: [],
  selectedPM: null,
  clearCalcTimestamp: 0, // increments to signal clearing calc results

  // counter used to notify components that counterparty was cleared
  counterpartyClearCounter: 0,

  // helper to clear counterparty selection and notify listeners
  clearCounterparty: () => set((state) => ({
    tradeEntryData: { ...state.tradeEntryData, counterPartyId: '', counterparty: '' },
    fieldErrors: { ...state.fieldErrors, counterPartyId: false },
    counterpartyClearCounter: state.counterpartyClearCounter + 1,
  })),

  // CRUD helpers for list
  setTrades: (trades) => set({ trades }),
  addTrade: (trade) => set((state) => ({ 
    trades: [trade, ...state.trades] 
  })),
  updateTrade: (tradeId, updates) =>
    set((state) => ({
      trades: state.trades.map((t) => (t.tradeId === tradeId ? { ...t, ...updates } : t)),
    })),
  removeTrade: (tradeId) => set((state) => ({ 
    trades: state.trades.filter((t) => t.tradeId !== tradeId) 
  })),
  clearTrades: () => set({ trades: [] }),

  // WebSocket trade update handler
  handleTradeUpdate: (tradeData) => {
    const existingTradeIndex = get().trades.findIndex(t => t.tradeId === tradeData.tradeId);
    
    if (existingTradeIndex >= 0) {
      // Update existing trade
      get().updateTrade(tradeData.tradeId, tradeData);
    } else {
      // Add new trade
      get().addTrade(tradeData);
    }
  },

  // Entry form helpers (mirroring actualOrderStore)
  setTradeEntryData: (data) => set((state) => ({ tradeEntryData: { ...state.tradeEntryData, ...data } })),
  setFieldErrors: (errors) => set({ fieldErrors: errors }),
  clearFieldErrors: () => set({ fieldErrors: {} }),
  clearFieldError: (fieldName) => set((state) => ({
    fieldErrors: { ...state.fieldErrors, [fieldName]: false },
  })),
  setSelectedPM: (pm) => set({ selectedPM: pm }),

  resetTradeEntryData: () =>
    set((state) => ({
      tradeEntryData: {
        account: '',
        cusip: '',
        cusipRef: '',
        side: '',
        tradeQuantity: '',
        tradePrice: '',
        executionDateTime: getCurrentUTCDateTimeLocalString(), // <-- reset to current UTC-only datetime
        counterPartyId: '',
        settlementDate: '',
        comments: '',
        accruedInterest: '',
        principalAmount: '',
        estimatedTotal: '',
        // pmId: null,
        userId: null,
        pm: '',
        bondInventoryId: null,
        secMasterId: null,
        tradeTypeCode: 1,
      },
      fieldErrors: {
        account: false,
        cusip: false,
        side: false,
        tradeQuantity: false,
        tradePrice: false,
        executionDateTime: false,
        counterPartyId: false,
        settlementDate: false,
        accruedInterest: false,
        principalAmount: false,
        estimatedTotal: false,
        // pmId: false,
      },
      // Preserve existing option lists:
      accountIdentifiers: state.accountIdentifiers, // keep
      pmUsers: state.pmUsers,        
      bondDetails: null, // ensure bond details cleared
      isEntryLoading: false,
      cusipError: '',
      selectedPM: null,
      clearCalcTimestamp: state.clearCalcTimestamp + 1, // trigger calc results clear
    })),

  fetchAccounts: async (token) => {
    try {
      const response = await fetchAccounts(token);
      const identifiers = response.map((acc) => acc.accountIdentifier);
      set({ accountIdentifiers: identifiers });
      if (identifiers.length > 0) {
        get().setTradeEntryData({ account: identifiers[0] });
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      set({ accountIdentifiers: [] });
    }
  },

  fetchPMUsers: async (token) => {
    try {
      const pmUsersData = await fetchPMUsersApi(token);
      set({ pmUsers: pmUsersData });
    } catch (error) {
      console.error('Error fetching PM users:', error);
      set({ pmUsers: [] });
    }
  },

  fetchPMAccounts: async (token, pmId) => {
    try {
      const pmAccountsData = await fetchPMAccountsApi(token, pmId);
      const accountIdentifiers = pmAccountsData.map((acc) => acc.accountIdentifier);
      set({ accountIdentifiers });
      const current = get().tradeEntryData;
      if (accountIdentifiers.length > 0 && !current.account) {
        get().setTradeEntryData({ account: accountIdentifiers[0] });
        get().clearFieldError('account');
      }
    } catch (error) {
      console.error('Error fetching PM accounts:', error);
      set({ accountIdentifiers: [] });
    }
  },

  validateCusip: async (token) => {
    const { cusip, cusipRef } = get().tradeEntryData;
    if (!cusip) {
      set({ cusipError: 'CUSIP must be at least 9 characters' });
      // ensure cusipRef cleared if cusip empty
      get().setTradeEntryData({ cusipRef: '' });
      return;
    }
    // If we already have an API canonical cusip and it matches the typed cusip (case-insensitive),
    // normalize the field and skip re-validating the same value.
    if (cusipRef && typeof cusipRef === 'string' && cusipRef.toLowerCase() === String(cusip).toLowerCase()) {
      // ensure store uses canonical cusip
      get().setTradeEntryData({ cusip: cusipRef });
      set({ cusipError: '' });
      return;
    }
    if (cusip.length < 9) {
      set({ cusipError: 'CUSIP must be at least 9 characters' });
      // Clear any stored cusipRef for too-short input
      get().setTradeEntryData({ cusipRef: '' });
      return;
    }

    set({ isEntryLoading: true });
    try {
      const bondDetails = await validateSecurityCusip(token, cusip);
      
      if (bondDetails?.success === false) {
        // Clear cusipRef on invalid API response
        get().setTradeEntryData({ cusipRef: '' });
        set({ cusipError: bondDetails.message || 'Invalid CUSIP' });
        return;
      }
      
      if (bondDetails && bondDetails.id) {
        const currentBondInventoryId = get().tradeEntryData?.bondInventoryId;
        // Only set bondInventoryId when it's not already present (null, undefined, or empty string)
        if (currentBondInventoryId === undefined || currentBondInventoryId === null || currentBondInventoryId === '') {
          get().setTradeEntryData({ bondInventoryId: bondDetails.id });
        }
      }
      // Fix: Extract secMasterId from secMasterDTO object
      if (bondDetails && bondDetails.secMasterDTO && bondDetails.secMasterDTO.secMasterId) {
        get().setTradeEntryData({ secMasterId: bondDetails.secMasterDTO.secMasterId });
      }
      // If API returned a canonical cusip, normalize and persist it
      if (bondDetails && bondDetails.cusip) {
        get().setTradeEntryData({ cusip: bondDetails.cusip, cusipRef: bondDetails.cusip });
      } else {
        // ensure cusipRef cleared if API did not provide a canonical value
        get().setTradeEntryData({ cusipRef: '' });
      }

      if (bondDetails && bondDetails.minQty != null) {
        const minQtyVal = bondDetails.minQty;
        get().setTradeEntryData({ minQty: minQtyVal });
        const currentQty = get().tradeEntryData.qty;
        if (!currentQty || Number(currentQty) < Number(minQtyVal)) {
          get().setTradeEntryData({ qty: `${minQtyVal}`, tradeQuantity: `${minQtyVal}` });
        }
      }
      set({ bondDetails, cusipError: '' });
    } catch (error) {
      // Clear cusipRef on exception
      get().setTradeEntryData({ cusipRef: '' });
      set({ cusipError: 'Invalid CUSIP or error fetching bond details' });
    } finally {
      set({ isEntryLoading: false });
    }
  },

  // Replace dummy data fetcher with real API call placeholder
  fetchTrades: async (token) => {
    set({ loading: true, error: null });
    try {
      // TODO: Replace with real API call when backend endpoint is ready
      // const response = await fetch(`${process.env.REACT_APP_API_URL}/trades`, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      // const trades = await response.json();
      // set({ trades });
      
      // For now, start with empty array - trades will come from WebSocket
      set({ trades: [] });
    } catch (e) {
      set({ error: e?.message || 'Failed to load trades' });
    } finally {
      set({ loading: false });
    }
  },
}));